/**
 * CreatorDashboardPage — Creator's token portfolio and performance dashboard.
 *
 * States:
 * - Connected with tokens: summary + token list
 * - Connected with no tokens: empty state with CTA to /launch
 * - Disconnected: connect wallet prompt
 * - Error: retry prompt
 * - Loading: skeleton
 *
 * Story 6.2 — Task 5
 */

import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";

import { CreatorTokenList } from "../components/creator/CreatorTokenList";
import { DashboardSummary } from "../components/creator/DashboardSummary";
import { DataFreshness } from "../components/ui/DataFreshness";
import { EmptyState } from "../components/ui/EmptyState";
import { useCreatorTokens } from "../hooks/api/use-creator-tokens";
import { useWallet } from "../hooks/use-wallet";
import { fadeInUp } from "../lib/animations";

export function CreatorDashboardPage() {
	const { isConnected, login } = useWallet();
	const { data, isLoading, error, refetch } = useCreatorTokens();

	// Disconnected state: prompt to connect wallet
	if (!isConnected) {
		return (
			<motion.div
				variants={fadeInUp}
				initial="hidden"
				animate="visible"
				className="mx-auto max-w-7xl px-4 py-16 text-center"
			>
				<EmptyState
					title="Connect Your Wallet"
					description="Connect your wallet to view your creator dashboard and manage your tokens."
					action={
						<button
							type="button"
							onClick={login}
							className="rounded-lg bg-pastel-vibrant-blue px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
						>
							Connect Wallet
						</button>
					}
				/>
			</motion.div>
		);
	}

	// Error state
	if (error && !data) {
		return (
			<motion.div
				variants={fadeInUp}
				initial="hidden"
				animate="visible"
				className="mx-auto max-w-7xl px-4 py-16 text-center"
			>
				<EmptyState
					title="Unable to load dashboard"
					description={error.message}
					action={
						<button
							type="button"
							onClick={() => {
								refetch().catch(() => {});
							}}
							className="rounded-lg bg-pastel-vibrant-blue px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
						>
							Retry
						</button>
					}
				/>
			</motion.div>
		);
	}

	const tokens = data?.tokens ?? [];
	const meta = data?.meta;

	return (
		<motion.div
			variants={fadeInUp}
			initial="hidden"
			animate="visible"
			className="mx-auto max-w-7xl px-4 py-8"
		>
			<div className="flex items-center justify-between mb-6">
				<h1 className="text-2xl font-bold text-pastel-black">Creator Dashboard</h1>
				{meta && !isLoading && tokens.length > 0 && (
					<DataFreshness
						source={meta.dataSource}
						updatedAt={new Date(Date.now() - meta.dataAge * 1000)}
					/>
				)}
			</div>

			<DashboardSummary tokens={tokens} isLoading={isLoading} />

			<div className="mt-8">
				{isLoading ? (
					<CreatorTokenList tokens={[]} isLoading />
				) : tokens.length === 0 ? (
					<EmptyState
						title="No tokens yet"
						description="You haven't created any tokens. Launch your first token to get started."
						action={
							<Link
								to="/launch"
								className="inline-block rounded-lg bg-pastel-vibrant-blue px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
							>
								Create Your First Token
							</Link>
						}
					/>
				) : (
					<CreatorTokenList tokens={tokens} isLoading={false} />
				)}
			</div>
		</motion.div>
	);
}
